const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to make API requests
const makeRequest = async (endpoint, options = {}) => {
  try {
    const token = localStorage.getItem('authToken');
    
    // Check if token exists
    if (!token) {
      throw new Error('No authentication token found. Please login first.');
    }
    
    const fullUrl = `${BASE_URL}${endpoint}`;
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const requestOptions = {
      method: options.method || 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      signal: controller.signal
    };
    
    // Include body only for POST, PUT, PATCH requests
    if (options.body && ['POST', 'PUT', 'PATCH'].includes(requestOptions.method)) {
      requestOptions.body = options.body;
    }
    
    const response = await fetch(fullUrl, requestOptions);
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = '';

      try {
        const errorJson = JSON.parse(errorText);
        // Click-to-call (and other) APIs return { msg, metadata: { api_status } }; use msg for user-facing error
        if (errorJson.msg && typeof errorJson.msg === 'string') {
          errorMessage = errorJson.msg;
        } else if (errorJson.msg?.error) {
          errorMessage = typeof errorJson.msg.error === 'string' ? errorJson.msg.error : JSON.stringify(errorJson.msg.error);
        } else if (errorJson.msg?.message) {
          errorMessage = errorJson.msg.message;
        }
      } catch {
        errorMessage = errorText || `Server returned ${response.status} ${response.statusText}`;
      }

      const err = new Error(errorMessage || `Request failed (${response.status})`);
      err.status = response.status;
      throw err;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - backend may not be running');
    }
    
    throw error;
  }
};

// API role path for click-to-call: Support -> support, Ops -> ops
const getClickToCallApiRole = (role) => {
  if (role === 'Support') return 'support';
  if (role === 'Ops') return 'ops';
  return null;
};

/**
 * Initiate click-to-call for a subscriber
 *
 * @param {string} role - User role (Support or Ops)
 * @param {string} applicationId - Subscriber's application ID (UUID)
 * @returns {Promise<object>} API response with success message
 * @throws {Error} If role is not authorized, API call fails, or validation errors occur
 */
export const initiateClickToCall = async (role, applicationId) => {
  const apiRole = getClickToCallApiRole(role);
  if (!apiRole) {
    throw new Error('Access denied. Only Support and Ops roles can initiate calls.');
  }

  // Validate applicationId
  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  // Support: /v4/support/applications/... ; Ops: /v4/ops/applications/...
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/click-to-call/`;

  return makeRequest(endpoint, {
    method: 'POST'
    // No body; application_id in URL; agent mobile from logged-in user. Response has msg + metadata.api_status only (no caller A/B).
  });
};

/**
 * Trigger the application-URL SMS to a subscriber.
 *
 * @param {string} role - User role (Support or Ops)
 * @param {string} applicationId - Subscriber's application ID (UUID)
 * @returns {Promise<object>} API response with { msg, metadata: { api_status } }
 * @throws {Error} If role is not authorized, applicationId missing, or the API call fails
 */
export const sendApplicationUrlSms = async (role, applicationId) => {
  const apiRole = getClickToCallApiRole(role);
  if (!apiRole) {
    throw new Error('Access denied. Only Support and Ops roles can send SMS.');
  }

  if (!applicationId) {
    throw new Error('Application ID is required');
  }

  // Support: /v4/support/applications/... ; Ops: /v4/ops/applications/...
  const endpoint = `/internal/apis/v4/${apiRole}/applications/${applicationId}/send-application-url-sms/`;

  return makeRequest(endpoint, {
    method: 'POST'
  });
};
